import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLenis } from "lenis/react";
import { Instagram, Facebook, Youtube, Menu, X, ArrowRight } from "lucide-react";
import { resolveUrl } from "../../lib/media";
import { FestivalTypography } from "./FestivalTypography";

// Festival-dedicated premium header (distinct from the main site Navbar).
// Nav items scroll to on-page sections for step 1; external ones use routes.
const NAV = [
  { label: "Éditions", section: "editions" },
  { label: "Films", section: "films" },
  { label: "Palmarès", to: "/festival/palmares" },
  { label: "Participer", action: "apply" },
  { label: "Actualités", to: "/festival/actualites" },
  { label: "À propos", to: "/festival/a-propos" },
];

const scrollToSection = (id, lenis) => {
  const el = document.getElementById(id);
  if (!el) return false;
  if (lenis) lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
};

export const FestivalHeader = ({ brand = {}, socials = [], onApply }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const socialIcon = (platform) => {
    const p = (platform || "").toLowerCase();
    if (p.includes("insta")) return Instagram;
    if (p.includes("face")) return Facebook;
    if (p.includes("you")) return Youtube;
    return null;
  };
  const socialLinks = (socials || []).filter((s) => s.url && socialIcon(s.platform));

  const handleNav = (item) => {
    setOpen(false);
    if (item.action === "apply") return onApply?.();
    if (item.section) {
      // Sections (Éditions / Films) live on the Festival home page only.
      // Scroll in place if already there, otherwise navigate home then scroll.
      if (location.pathname === "/festival") scrollToSection(item.section, lenis);
      else navigate("/festival", { state: { scrollTo: item.section } });
    }
  };

  return (
    <header
      data-testid="festival-header"
      className={`festival-universe fixed top-0 inset-x-0 z-[9000] transition-colors duration-500 ${scrolled ? "bg-[#050505]/85 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-black/70 to-transparent"}`}>
      <FestivalTypography />
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 h-[74px] flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" data-testid="festival-header-logo" className="flex items-center gap-3 shrink-0 group">
          <img src={resolveUrl(brand.logo) || "/karams-logo.png"} alt="Karams Studio" className="h-9 w-auto" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-sans font-bold tracking-[0.1em] text-white text-[15px]">KARAMS</span>
            <span className="text-[10px] tracking-[0.4em] text-white/50">STUDIO</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="fx-nav hidden lg:flex items-center gap-7">
          {NAV.map((item) =>
            item.to ? (
              <Link key={item.label} to={item.to} data-testid={`festival-nav-${item.label.toLowerCase()}`}
                className="text-[12px] tracking-[0.16em] uppercase text-white/70 hover:text-electric transition-colors">{item.label}</Link>
            ) : (
              <button key={item.label} onClick={() => handleNav(item)} data-testid={`festival-nav-${item.label.toLowerCase()}`}
                className="text-[12px] tracking-[0.16em] uppercase text-white/70 hover:text-electric transition-colors">{item.label}</button>
            ),
          )}
        </nav>

        {/* Right: socials + CTA */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden md:flex items-center gap-1.5">
            {socialLinks.map((s, i) => {
              const Icon = socialIcon(s.platform);
              return (
                <a key={i} href={s.url} target="_blank" rel="noreferrer" aria-label={s.platform}
                  data-testid={`festival-social-${(s.platform || "").toLowerCase()}`}
                  className="h-9 w-9 rounded-full border border-white/15 text-white/70 flex items-center justify-center hover:border-electric hover:text-electric transition-colors">
                  <Icon size={16} />
                </a>
              );
            })}
          </div>
          <button onClick={onApply} data-testid="festival-header-apply"
            className="hidden sm:inline-flex items-center gap-2 px-5 h-10 rounded-md bg-electric text-[11px] tracking-[0.14em] uppercase font-semibold hover:brightness-110 transition-all" style={{ color: "var(--fx-on-primary)" }}>
            Soumettre ma candidature
          </button>
          <button onClick={() => setOpen((v) => !v)} data-testid="festival-header-menu-toggle" aria-label="Menu"
            className="lg:hidden h-10 w-10 rounded-full border border-white/15 text-white flex items-center justify-center">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#050505] border-t border-white/10 px-6 py-6" data-testid="festival-mobile-menu">
          <nav className="fx-nav flex flex-col gap-1">
            {NAV.map((item) =>
              item.to ? (
                <Link key={item.label} to={item.to} onClick={() => setOpen(false)} data-testid={`festival-mobile-nav-${item.label.toLowerCase()}`}
                  className="py-3 text-sm tracking-[0.14em] uppercase text-white/80 border-b border-white/5">{item.label}</Link>
              ) : (
                <button key={item.label} onClick={() => handleNav(item)} data-testid={`festival-mobile-nav-${item.label.toLowerCase()}`}
                  className="py-3 text-left text-sm tracking-[0.14em] uppercase text-white/80 border-b border-white/5">{item.label}</button>
              ),
            )}
          </nav>
          <button onClick={() => { setOpen(false); onApply?.(); }} data-testid="festival-mobile-apply"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 h-11 rounded-md bg-electric text-[11px] tracking-[0.14em] uppercase font-semibold" style={{ color: "var(--fx-on-primary)" }}>
            Soumettre ma candidature <ArrowRight size={14} />
          </button>
        </div>
      )}
    </header>
  );
};
